import { Auth0Client } from "@auth0/auth0-spa-js";

import CONFIG from "@/config";
import {
  CACHE_LOCATION,
  HTTP_TIMEOUT_SECONDS,
  SCOPE_STRING,
  assertAuthConfig,
} from "./authConfig";

export type Auth0ClientFactory = () => Promise<Auth0Client>;

const defaultFactory: Auth0ClientFactory = async () => {
  assertAuthConfig();
  // `new Auth0Client`, not `createAuth0Client`: the latter also runs an internal
  // `await checkSession()`, which would double-init because auth0-react's
  // Auth0Provider runs its own mount-time checkSession. The provider is the
  // single init driver.
  return new Auth0Client({
    domain: CONFIG.auth.domain,
    clientId: CONFIG.auth.clientId,
    authorizationParams: {
      audience: CONFIG.auth.audience,
      redirect_uri: CONFIG.auth.redirectUri,
      scope: SCOPE_STRING,
    },
    cacheLocation: CACHE_LOCATION,
    useRefreshTokens: true,
    useRefreshTokensFallback: false,
    httpTimeoutInSeconds: HTTP_TIMEOUT_SECONDS,
  });
};

let factory: Auth0ClientFactory = defaultFactory;
let clientPromise: Promise<Auth0Client> | null = null;

/**
 * The promise is assigned synchronously so that concurrent callers share one
 * client. Two clients would mean two independent token caches racing the same
 * rotating refresh token.
 *
 * A rejection clears the memo so a later attempt can retry; without that, one
 * transient failure at boot would poison every subsequent call for the life of
 * the page.
 */
export const getClient = (): Promise<Auth0Client> => {
  if (!clientPromise) {
    clientPromise = factory().catch((error) => {
      clientPromise = null;
      throw error;
    });
  }
  return clientPromise;
};

export const __setClientFactory = (next: Auth0ClientFactory): void => {
  factory = next;
  clientPromise = null;
};

export const __reset = (): void => {
  factory = defaultFactory;
  clientPromise = null;
};
